// @core/stores/notifications/NotificationsStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { notificationsApi } from '@core/api';
import type {
  CreateNotificationScheduledRequest,
  CreateNotificationScheduledResponse,
  ListNotificationsScheduledResponse,
  Notification,
  ProcessDeliveryConfirmationRequest,
  SendNotificationRequest,
  SendNotificationResponse,
  SendNotificationSmartRequest,
  SendNotificationSmartResponse,
  SendNotificationsBulkRequest,
  SendNotificationsBulkResponse,
  SetNotificationReadRequest,
  SetNotificationsReadAllRequest,
} from '@core/types/notifications';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class NotificationsStore {
  // List state
  items: Notification[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Notification | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Notification>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Notification | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Notification | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Cancel scheduled notification
   */
  async CancelNotificationScheduled(schedule_id: string): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await notificationsApi.CancelNotificationScheduled(schedule_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create scheduled notification
   */
  async CreateNotificationScheduled(data: CreateNotificationScheduledRequest): Promise<CreateNotificationScheduledResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await notificationsApi.CreateNotificationScheduled(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get notification by ID
   */
  async GetNotification(notification_id: string): Promise<Notification> {
    // Check cache first
    const cached = this.cache.get(notification_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await notificationsApi.GetNotification(notification_id);
      const item = response.notification;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List notifications with filters
   */
  async ListNotifications(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await notificationsApi.ListNotifications(params);

      runInAction(() => {
        this.items = response.notifications?.items ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List notifications for user
   */
  async ListNotificationsByUser(user_id: string, params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await notificationsApi.ListNotificationsByUser(user_id, params);

      runInAction(() => {
        this.items = response.notifications?.items ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List scheduled notifications
   */
  async ListNotificationsScheduled(params?: PaginatedRequest): Promise<ListNotificationsScheduledResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await notificationsApi.ListNotificationsScheduled(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Process delivery confirmation
   */
  async ProcessDeliveryConfirmation(data: ProcessDeliveryConfirmationRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await notificationsApi.ProcessDeliveryConfirmation(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Send a notification
   */
  async SendNotification(data: SendNotificationRequest): Promise<SendNotificationResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await notificationsApi.SendNotification(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Send smart notification based on user preferences
   */
  async SendNotificationSmart(data: SendNotificationSmartRequest): Promise<SendNotificationSmartResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await notificationsApi.SendNotificationSmart(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Send notifications in bulk
   */
  async SendNotificationsBulk(data: SendNotificationsBulkRequest): Promise<SendNotificationsBulkResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await notificationsApi.SendNotificationsBulk(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Mark notification as read
   */
  async SetNotificationRead(notification_id: string, data: SetNotificationReadRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await notificationsApi.SetNotificationRead(notification_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Mark all notifications as read
   */
  async SetNotificationsReadAll(user_id: string, data: SetNotificationsReadAllRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await notificationsApi.SetNotificationsReadAll(user_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const notificationsStore = new NotificationsStore();
